import { useMemo, useRef, useState } from 'react';
import { parseNumber } from './calc';
import { CONF_LEVELS, calibSummary, makeCalibRound, type IntervalAnswer, type PairAnswer } from './calib';
import { formatValue } from './facts';
import { elapsed, type DrillProps } from './meta';

const confLabel = (c: number) => (c === 50 ? '50% 찍기' : c === 99 ? '99% 확실' : `${c}%`);

/** 비교 문제: 고르기 → 확신 걸기 → 정답과 실제 값. 구간 문제: 90% 범위 적기 → 정답이 안에 드는지 */
export function CalibDrill({ level, seed, onDone }: DrillProps) {
  const items = useMemo(() => makeCalibRound(level, seed), [level, seed]);
  const started = useRef(performance.now());
  const answers = useRef<PairAnswer[]>([]);
  const ranges = useRef<IntervalAnswer[]>([]);
  const [index, setIndex] = useState(0);
  const [side, setSide] = useState<'a' | 'b' | null>(null);
  const [lo, setLo] = useState('');
  const [hi, setHi] = useState('');
  const [feedback, setFeedback] = useState<{ hit: boolean; text: string } | null>(null);
  const item = items[index];
  const last = index + 1 >= items.length;

  const next = () => {
    if (last) {
      const pairs = answers.current;
      onDone({
        correct: pairs.filter((a) => a.hit).length,
        total: pairs.length,
        duration_ms: elapsed(started.current),
        detail: { seed, pairs, intervals: ranges.current },
        summary: calibSummary(pairs, ranges.current),
      });
      return;
    }
    setSide(null);
    setLo('');
    setHi('');
    setFeedback(null);
    setIndex(index + 1);
  };

  const bet = (conf: number) => {
    if (item.kind !== 'pair' || !side || feedback) return;
    const hit = side === item.answer;
    answers.current.push({ id: item.id, conf, hit, level: item.level });
    setFeedback({ hit, text: `확신 ${conf}% → ${hit ? '맞음' : '틀림'}` });
  };

  const low = parseNumber(lo);
  const high = parseNumber(hi);
  const rangeReady = low !== null && high !== null && low <= high;
  const submitRange = () => {
    if (item.kind !== 'interval' || low === null || high === null || low > high || feedback) return;
    const v = item.fact.v;
    const hit = low <= v && v <= high;
    ranges.current.push({ id: item.id, lo: low, hi: high, v, hit });
    setFeedback({ hit, text: `정답 ${formatValue(item.fact)}${item.fact.note ? ` (${item.fact.note})` : ''}` });
  };

  return (
    <div className={`drill calib-drill ${feedback ? (feedback.hit ? 'ok' : 'miss') : ''}`}>
      <div className="row-between">
        <span className="drill-progress">{index + 1} / {items.length}</span>
        <span className="small muted">{item.kind === 'pair' ? '고르고 확신 걸기' : '90% 확신 범위'}</span>
      </div>
      {item.kind === 'pair' ? (
        <div className="calib-q">
          <p className="calib-prompt">{item.prompt}</p>
          <div className="choice-list calib-sides">
            {(['a', 'b'] as const).map((k) => {
              const f = item[k];
              const cls = feedback ? (k === item.answer ? 'right' : side === k ? 'wrong' : '') : side === k ? 'picked' : '';
              return (
                <button key={k} type="button" className={`choice ${cls}`} disabled={side !== null} onClick={() => setSide(k)}>
                  {f.name}
                  {feedback && <span className="calib-val">{formatValue(f)}</span>}
                </button>
              );
            })}
          </div>
          {side && !feedback && (
            <>
              <p className="calib-ask">얼마나 확신하나요?</p>
              <div className="conf-row">
                {CONF_LEVELS.map((c) => (
                  <button key={c} type="button" className="choice conf" onClick={() => bet(c)}>{confLabel(c)}</button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <form className="calib-q" onSubmit={(e) => { e.preventDefault(); submitRange(); }}>
          <p className="calib-prompt">{item.question}</p>
          <p className="small muted">정답이 10번 중 9번은 들어갈 범위를 적으세요. 너무 좁으면 과신, 너무 넓으면 쓸모없는 답입니다.</p>
          <div className="range-row">
            <input
              key={`lo${index}`} className="input" inputMode="decimal" autoComplete="off" autoFocus aria-label="최소" placeholder="최소"
              value={lo} onChange={(e) => setLo(e.target.value)} disabled={!!feedback}
            />
            <span aria-hidden="true">~</span>
            <input
              key={`hi${index}`} className="input" inputMode="decimal" autoComplete="off" aria-label="최대" placeholder="최대"
              value={hi} onChange={(e) => setHi(e.target.value)} disabled={!!feedback}
            />
            <span className="calc-unit">{item.fact.unit}</span>
          </div>
          {!feedback && (
            <div className="drill-end">
              <button type="submit" className="btn btn-primary" disabled={!rangeReady}>확인</button>
            </div>
          )}
        </form>
      )}
      <div aria-live="polite">
        {feedback && (
          <>
            <p className="rule-feedback">{item.kind === 'pair' ? (feedback.hit ? '✓ 맞음' : '✗ 틀림') : feedback.hit ? '✓ 구간 안' : '✗ 구간 밖'}</p>
            <p className="seq-explain">{feedback.text}</p>
            <div className="drill-end">
              <button type="button" className="btn btn-primary" autoFocus onClick={next}>{last ? '결과 보기' : '다음'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
